import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type SetOrganisationVatTreatmentRequest,
  type VatTreatmentMatrix,
} from '../client';
import { organisationTaxRateKeys } from './useOrganisationTaxRates';

/** Stable query-key factory for the VAT treatment matrix. */
export const organisationVatTreatmentKeys = {
  all: ['directory', 'organisation-vat-treatment'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-vat-treatment', 'org', organisationId] as const,
};

/** The VAT treatment matrix for an organisation. Pass `null` to keep disabled. */
export function useOrganisationVatTreatments(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<VatTreatmentMatrix, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<VatTreatmentMatrix, Error>({
    queryKey: organisationId
      ? organisationVatTreatmentKeys.byOrganisation(organisationId)
      : organisationVatTreatmentKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationVatTreatments(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Set one line. The server answers with the whole matrix, which replaces the cache. */
export function useSetOrganisationVatTreatment() {
  const qc = useQueryClient();
  return useMutation<VatTreatmentMatrix, Error, SetOrganisationVatTreatmentRequest>({
    mutationKey: [...organisationVatTreatmentKeys.all, 'set'],
    mutationFn: (body) => directoryApi.setOrganisationVatTreatment(body),
    onSuccess: (matrix, vars) => {
      qc.setQueryData(organisationVatTreatmentKeys.byOrganisation(vars.organisationId), matrix);
    },
  });
}

/**
 * "Set defaults". May add tax schemas the organisation was missing (Outside
 * scope, say), so the tax-schema list is refreshed too.
 */
export function useSetOrganisationVatTreatmentDefaults() {
  const qc = useQueryClient();
  return useMutation<VatTreatmentMatrix, Error, { organisationId: string }>({
    mutationKey: [...organisationVatTreatmentKeys.all, 'set-defaults'],
    mutationFn: ({ organisationId }) => directoryApi.setOrganisationVatTreatmentDefaults(organisationId),
    onSuccess: (matrix, vars) => {
      qc.setQueryData(organisationVatTreatmentKeys.byOrganisation(vars.organisationId), matrix);
      qc.invalidateQueries({ queryKey: organisationTaxRateKeys.byOrganisation(vars.organisationId) });
    },
  });
}
